/**
 * TimerManager - Turn-Countdown für den Multiplayer-Client
 * Kapselt setInterval (100ms), Delta-Math und Restzeit-Berechnung.
 * Verhalten identisch zur vorherigen Inline-Implementierung im MultiplayerClient.
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
export class TimerManager {
    /**
     * @param {(remaining: number, limit: number) => void} [onUpdate] - Wird bei jedem Tick
     *        mit (turnTimeRemaining, turnTimeLimit) aufgerufen.
     */
    constructor(onUpdate = null) {
        this.onTurnTimeUpdate = onUpdate;

        this.turnTimeInterval = null;
        this.turnTimeRemaining = -1;
        this.turnTimeLimit = 0;
        this.turnStartTime = null;
    }

    /**
     * Setzt die Timer-Daten und startet den Update-Interval.
     * @param {number} turnTimeLimit - Zeitlimit in Sekunden (<=0 = unbegrenzt)
     * @param {number} turnStartTime - Startzeitpunkt (ms-Timestamp)
     */
    start(turnTimeLimit, turnStartTime) {
        this.turnTimeLimit = turnTimeLimit || 0;
        this.turnStartTime = turnStartTime;

        this.stop();

        // Kein Timer wenn unbegrenzt
        if (!this.turnTimeLimit || this.turnTimeLimit <= 0) {
            this.turnTimeRemaining = -1;
            return;
        }

        // Initial berechnen
        this.update();

        // Alle 100ms aktualisieren für flüssige Anzeige
        this.turnTimeInterval = setInterval(() => {
            this.update();
        }, 100);
    }

    /**
     * Stoppt den Timer-Update-Interval
     */
    stop() {
        if (this.turnTimeInterval) {
            clearInterval(this.turnTimeInterval);
            this.turnTimeInterval = null;
        }
    }

    /**
     * Berechnet und aktualisiert die verbleibende Zeit
     */
    update() {
        if (!this.turnTimeLimit || this.turnTimeLimit <= 0 || !this.turnStartTime) {
            this.turnTimeRemaining = -1;
            return;
        }

        const elapsed = (Date.now() - this.turnStartTime) / 1000;
        this.turnTimeRemaining = Math.max(0, this.turnTimeLimit - elapsed);

        if (this.onTurnTimeUpdate) {
            this.onTurnTimeUpdate(this.turnTimeRemaining, this.turnTimeLimit);
        }
    }

    /**
     * Gibt die verbleibende Zeit in Sekunden zurück
     * @returns {number} Sekunden, -1 wenn unbegrenzt
     */
    getRemaining() {
        if (!this.turnTimeLimit || this.turnTimeLimit <= 0) {
            return -1;
        }
        return this.turnTimeRemaining;
    }

    /**
     * Räumt den Timer auf (Memory Leak Prevention)
     */
    dispose() {
        this.stop();
        this.onTurnTimeUpdate = null;
    }
}

export default TimerManager;
